import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import styles from "./RequestAssignmentForm.module.css";

function RequestAssignmentForm() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [requests, setRequests] = useState([]);
  const [cars, setCars] = useState([]);
  const [drivers, setDrivers] = useState([]);

  const [selectedRequestId, setSelectedRequestId] = useState("");
  const [selectedCarId, setSelectedCarId] = useState("");
  const [selectedDriverId, setSelectedDriverId] = useState("");

  const [requestDescription, setRequestDescription] = useState("");

  const [loadingRequests, setLoadingRequests] = useState(true);
  const [loadingDependencies, setLoadingDependencies] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [successOpen, setSuccessOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState(
    "Данные успешно отправлены"
  );

  const [errorTitle, setErrorTitle] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const clearError = () => {
    setErrorTitle("");
    setErrorMessage("");
  };

  useEffect(() => {
    if (!currentUser) navigate("/");
  }, [currentUser, navigate]);

  async function safeReadResponse(response) {
    const ct = response.headers.get("content-type") || "";
    if (ct.includes("application/json")) {
      try {
        return await response.json();
      } catch {
        return {};
      }
    }

    try {
      const text = await response.text();
      return { details: text };
    } catch {
      return {};
    }
  }

  function normalizeErrorMessage(status, data) {
    const raw =
      data?.details ||
      data?.error ||
      data?.message ||
      (typeof data === "string" ? data : "") ||
      "";

    if (status === 403) return raw || "Недостаточно прав";
    if (status === 401) return raw || "Требуется авторизация";
    if (status >= 500) return raw || "Ошибка сервера";
    return raw || "Ошибка запроса";
  }

  const selectedRequest = useMemo(() => {
    return (
      requests.find((item) => String(item.id) === String(selectedRequestId)) ||
      null
    );
  }, [requests, selectedRequestId]);

  useEffect(() => {
    let cancelled = false;

    const loadRequests = async () => {
      try {
        setLoadingRequests(true);
        clearError();

        const resp = await fetch("/api/request-assignment/requests", {
          method: "GET",
          credentials: "include",
          headers: { Accept: "application/json" },
        });

        const data = await safeReadResponse(resp);

        if (!resp.ok) {
          if (!cancelled) {
            setRequests([]);
            setErrorTitle("Не удалось загрузить заявки");
            setErrorMessage(normalizeErrorMessage(resp.status, data));
          }
          return;
        }

        const list = Array.isArray(data?.requests) ? data.requests : [];

        if (!cancelled) {
          setRequests(list);

          if (list.length === 1) {
            const only = list[0];
            setSelectedRequestId(String(only.id || ""));
            setRequestDescription(only.description || "");
          }
        }
      } catch (err) {
        if (!cancelled) {
          setRequests([]);
          setErrorTitle("Ошибка сети");
          setErrorMessage(
            "Не удалось загрузить список заявок. " + (err?.message || "")
          );
        }
      } finally {
        if (!cancelled) setLoadingRequests(false);
      }
    };

    loadRequests();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadDependencies = async () => {
      if (!selectedRequestId) {
        setCars([]);
        setDrivers([]);
        setSelectedCarId("");
        setSelectedDriverId("");
        setRequestDescription("");
        return;
      }

      try {
        setLoadingDependencies(true);
        clearError();

        if (selectedRequest) {
          setRequestDescription(selectedRequest.description || "");
        }

        const resp = await fetch(
          `/api/request-assignment/request-details/${encodeURIComponent(
            selectedRequestId
          )}`,
          {
            method: "GET",
            credentials: "include",
            headers: { Accept: "application/json" },
          }
        );

        const data = await safeReadResponse(resp);

        if (!resp.ok) {
          if (!cancelled) {
            setCars([]);
            setDrivers([]);
            setSelectedCarId("");
            setSelectedDriverId("");
            setErrorTitle("Не удалось загрузить данные заявки");
            setErrorMessage(normalizeErrorMessage(resp.status, data));
          }
          return;
        }

        const nextCars = Array.isArray(data?.cars) ? data.cars : [];
        const nextDrivers = Array.isArray(data?.drivers) ? data.drivers : [];
        const nextDescription = selectedRequest?.description || "";

        if (!cancelled) {
          setCars(nextCars);
          setDrivers(nextDrivers);
          setRequestDescription(nextDescription);

          setSelectedCarId(
            nextCars.length === 1 ? String(nextCars[0].id || "") : ""
          );
          setSelectedDriverId(
            nextDrivers.length === 1 ? String(nextDrivers[0].id || "") : ""
          );
        }
      } catch (err) {
        if (!cancelled) {
          setCars([]);
          setDrivers([]);
          setSelectedCarId("");
          setSelectedDriverId("");
          setErrorTitle("Ошибка сети");
          setErrorMessage(
            "Не удалось загрузить машины и водителей. " + (err?.message || "")
          );
        }
      } finally {
        if (!cancelled) setLoadingDependencies(false);
      }
    };

    loadDependencies();

    return () => {
      cancelled = true;
    };
  }, [selectedRequestId, selectedRequest]);

  useEffect(() => {
    if (!successOpen) return;
    const t = setTimeout(() => navigate("/dashboard"), 3000);
    return () => clearTimeout(t);
  }, [successOpen, navigate]);

  const isFormValid = useMemo(() => {
    return (
      String(selectedRequestId).trim() &&
      String(selectedCarId).trim() &&
      String(selectedDriverId).trim()
    );
  }, [selectedRequestId, selectedCarId, selectedDriverId]);

  const handleRequestChange = (e) => {
    setSelectedRequestId(e.target.value);
    setSelectedCarId("");
    setSelectedDriverId("");
    setCars([]);
    setDrivers([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    clearError();
    setSuccessOpen(false);

    if (!isFormValid || submitting) {
      setErrorTitle("Проверьте поля");
      setErrorMessage("Заполните все обязательные поля.");
      return;
    }

    try {
      setSubmitting(true);

      const payload = {
        sender: currentUser?.name,
        orderId: selectedRequestId,
        carId: selectedCarId,
        driverId: selectedDriverId,
      };

      const resp = await fetch("/api/request-assignment/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      const data = await safeReadResponse(resp);

      if (resp.ok) {
        const msg =
          data?.details || data?.message || "Данные успешно отправлены";
        setSuccessMessage(typeof msg === "string" ? msg : JSON.stringify(msg));
        setSuccessOpen(true);
      } else {
        setErrorTitle("Не удалось отправить");
        setErrorMessage(normalizeErrorMessage(resp.status, data));
      }
    } catch (err) {
      setErrorTitle("Ошибка сети");
      setErrorMessage(
        "Не удалось связаться с сервером. " + (err?.message || "")
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!currentUser) return <div>Загрузка...</div>;

  return (
    <div className={styles.container}>
      {successOpen && (
        <div className={styles.successOverlay} role="dialog" aria-modal="true">
          <div className={styles.successModal}>
            <div className={styles.successIcon} aria-hidden="true" />
            <h3>Успешно!</h3>
            <p>{successMessage}</p>
            <div className={styles.progressBar}>
              <div className={styles.progressFill} />
            </div>
            <p className={styles.redirectText}>
              Переход в кабинет через 3 секунды...
            </p>
          </div>
        </div>
      )}

      <div className={styles.formContainer}>
        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.requestRow}>
            <div className={styles.requestField}>
              
              <select
                id="requestSelect"
                value={selectedRequestId}
                onChange={handleRequestChange}
                className={styles.select}
                required
                disabled={loadingRequests || submitting}
              >
                <option value="" disabled>
                  {loadingRequests
                    ? "Загрузка заявок..."
                    : requests.length === 0
                    ? "Нет доступных заявок"
                    : "Выберите заявку"}
                </option>
                {requests.map((item, idx) => (
                  <option key={item.id || idx} value={item.id || ""}>
                    {item.name || `${idx + 1}`}
                  </option>
                ))}
              </select>
            </div>

            <button
              onClick={() => navigate("/dashboard")}
              className={styles.backButton}
              type="button"
            >
              Назад
            </button>
          </div>

          <div className={styles.inputField}>
            <label className={styles.label} htmlFor="requestDescription">
              Описание заявки:
            </label>
            <textarea
              id="requestDescription"
              className={`${styles.input} ${styles.textarea}`}
              value={requestDescription}
              readOnly
              placeholder="Описание заявки появится после выбора"
            />
          </div>

          <div className={styles.twoColumns}>
            <div className={styles.inputField}>
              <label className={styles.label} htmlFor="carSelect">
                Машина: *
              </label>
              <select
                id="carSelect"
                value={selectedCarId}
                onChange={(e) => setSelectedCarId(e.target.value)}
                className={styles.select}
                required
                disabled={!selectedRequestId || loadingDependencies || submitting}
              >
                <option value="" disabled>
                  {loadingDependencies ? "Загрузка машин..." : "Выберите машину"}
                </option>
                {cars.map((item, idx) => (
                  <option key={item.id || idx} value={item.id || ""}>
                    {item.name || `Машина ${idx + 1}`}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.inputField}>
              <label className={styles.label} htmlFor="driverSelect">
                Водитель: *
              </label>
              <select
                id="driverSelect"
                value={selectedDriverId}
                onChange={(e) => setSelectedDriverId(e.target.value)}
                className={styles.select}
                required
                disabled={!selectedRequestId || loadingDependencies || submitting}
              >
                <option value="" disabled>
                  {loadingDependencies
                    ? "Загрузка водителей..."
                    : "Выберите водителя"}
                </option>
                {drivers.map((item, idx) => (
                  <option key={item.id || idx} value={item.id || ""}>
                    {item.name || `Водитель ${idx + 1}`}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {(errorTitle || errorMessage) && (
            <div className={`${styles.alert} ${styles.alertError}`}>
              <div>
                <p className={styles.alertText}>{errorTitle || "Ошибка"}</p>
                {errorMessage && (
                  <p className={styles.alertSubText}>{errorMessage}</p>
                )}
              </div>
              <button
                type="button"
                className={styles.alertButton}
                onClick={clearError}
              >
                Понятно
              </button>
            </div>
          )}

          <button
            type="submit"
            className={styles.sendButton}
            disabled={!isFormValid || submitting || loadingDependencies}
          >
            {submitting ? "Отправка..." : "Отправить"}
          </button>
        </form>
      </div>
    </div>
  );
}

export default RequestAssignmentForm;